import { addDays, format, parse } from "date-fns";
import * as React from "react";
import { useFormContext, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Badge } from "@bcc/components/ui/badge";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { DateSelect } from "@bcc/components/ui/date-select";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@bcc/components/ui/form";
import { Input } from "@bcc/components/ui/input";
import { MultiSelect } from "@bcc/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { calculateMarginPercent, parseShipToCountries, resolveHomeRegionPricingContext } from "@bcc/lib/products/cross-border-tax";
import { cn } from "@bcc/lib/utils";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import { useConfigState } from "@client-shared/providers/config";
import { MAXIMUM_END_OF_STOCK_DATE_DAYS, MINIMUM_END_OF_STOCK_DATE_DAYS, REQUIRED_GRIND } from "../constants";
import type { BagSizeGroup, FormProduct, FormVariant } from "../types";
import { VariantInfoBlock } from "../variant-info-block";
import { CrossBorderVariantPricing } from "./cross-border-variant-pricing";
import type { SingleVariantFormProps } from "./single-variant-form.types";
import { handleNumericFieldChange, setSingleVariantFormTestId } from "./single-variant-form.utils";

interface MappedGrindMessage {
  key: string;
  text: string;
}

const EMPTY_MESSAGES: readonly MappedGrindMessage[] = [];
const REJECTED_VARIANT_STATUS = "Rejected";

type VariantStatusSource = Pick<FormProduct["variants"][number], "isImportedLocalVariant" | "status">;

const isLiveVariant = (variant: VariantStatusSource | undefined) => Boolean(variant?.isImportedLocalVariant);

const isRejectedVariant = (variant: VariantStatusSource | undefined) =>
  variant?.status === REJECTED_VARIANT_STATUS && !isLiveVariant(variant);

const toggleGrindSelection = (currentValue: string[], isChecked: boolean, grindValue: string): string[] =>
  isChecked ? currentValue.filter((v) => v !== grindValue) : [...currentValue, grindValue];

// Dates are excluded from the sync below for variants that are only
// `isBagSizeVariantImported` (a new, not-yet-imported grind sharing a bag size
// with an already-imported sibling) — those rows render an editable Start/End
// Date picker, and forcing it back to the already-imported sibling's own
// (often past) date on every change made the picker impossible to use. Callers
// only pass `shouldSyncDates: true` for fully hidden rows (`showAlertMessage`),
// where the date fields aren't rendered at all, so keeping them aligned there
// is safe.
const applySharedBagSizeDates = (form: UseFormReturn<FormProduct>, index: number, variant: FormVariant, bagSizeGroup: BagSizeGroup) => {
  if (bagSizeGroup.startDate !== undefined && variant.startDate !== bagSizeGroup.startDate) {
    form.setValue(`variants.${index}.startDate`, bagSizeGroup.startDate, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }
  if (variant.endDate !== bagSizeGroup.endDate) {
    form.setValue(`variants.${index}.endDate`, bagSizeGroup.endDate, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
  }
};

// Keep hidden same-bag-size variants aligned with the editable default-grind
// variant; otherwise stale invisible values can keep the form invalid.
const applySharedBagSizeValues = (
  form: UseFormReturn<FormProduct>,
  index: number,
  variant: FormVariant,
  bagSizeGroup: BagSizeGroup,
  shouldSyncDates: boolean
) => {
  if (bagSizeGroup.price !== undefined && variant.price !== bagSizeGroup.price) {
    form.setValue(`variants.${index}.price`, bagSizeGroup.price, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
  }
  if (bagSizeGroup.wholesalePrice !== undefined && variant.wholesalePrice !== bagSizeGroup.wholesalePrice) {
    form.setValue(`variants.${index}.wholesalePrice`, bagSizeGroup.wholesalePrice, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }
  if (shouldSyncDates) {
    applySharedBagSizeDates(form, index, variant, bagSizeGroup);
  }
  if (bagSizeGroup.term !== undefined && variant.term !== bagSizeGroup.term) {
    form.setValue(`variants.${index}.term`, bagSizeGroup.term, { shouldDirty: false, shouldTouch: false, shouldValidate: true });
  }
  if (bagSizeGroup.weeklyInventory !== undefined && variant.weeklyInventory !== bagSizeGroup.weeklyInventory) {
    form.setValue(`variants.${index}.weeklyInventory`, bagSizeGroup.weeklyInventory, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }
  if (variant.totalMaxShortTermInventory !== bagSizeGroup.totalMaxShortTermInventory) {
    form.setValue(`variants.${index}.totalMaxShortTermInventory`, bagSizeGroup.totalMaxShortTermInventory, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }
  // Ship-to is bag-size based: keep same-bag-size grinds aligned
  // with the whole-bean variant's value so cross-border pricing stays
  // consistent for the hidden/inherited grinds.
  if (bagSizeGroup.ship_to !== undefined && variant.ship_to !== bagSizeGroup.ship_to) {
    form.setValue(`variants.${index}.ship_to`, bagSizeGroup.ship_to, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }
};

export const SingleVariantForm = ({
  index,
  nextAvailableStartDay,
  productConfig,
  selectedVariantCount,
  singleBagSize,
  termConfig,
  grindConfig,
  bagSizeGroup,
  isOverrideGrindEnabled = false,
  isOverrideBagSizeEnabled = false,
  shortTermMaxWeeklyInventory = 200,
  shortTermMinWeeklyInventory = 50,
  longTermMinWeeklyInventory = 50,
  longTermMaxWeeklyInventory = 200,
  regionLargeBagSize,
  isProductImportedLocal = false,
  excludedBagSizes,
  homeRegion,
  taxRatesByRegion,
  shipToOptions,
}: SingleVariantFormProps) => {
  const { t } = useTranslation();
  const form = useFormContext<FormProduct>();
  const variant = form.watch(`variants.${index}`);

  // Cross-border pricing renders when a home region is supplied (i.e. a
  // cross-border vendor) and the bag size ships to more than one country, OR
  // for a DE-domestic vendor — resolveHomeRegionPricingContext synthesizes a
  // home-only ship-to list for the latter since domestic products don't
  // populate the cross-border ship-to field at all.
  // Ship-to is bag-size based: read the per-variant value (entered on the
  // whole-bean variant, inherited by other grinds) rather than a single
  // product-level field.
  const shipToValue = form.watch(`variants.${index}.ship_to`);
  const rawShipToCountries = React.useMemo(() => parseShipToCountries(shipToValue), [shipToValue]);
  const { shipTo: effectiveShipTo, showRegionPricingUi } = React.useMemo(
    () => resolveHomeRegionPricingContext(homeRegion, rawShipToCountries),
    [homeRegion, rawShipToCountries]
  );
  const crossBorderHomeRegion = homeRegion && effectiveShipTo.includes(homeRegion) ? homeRegion : undefined;
  const isCrossBorderPricing = showRegionPricingUi && !!crossBorderHomeRegion;

  // Get term for conditional rendering
  const term = form.watch(`variants.${index}.term`);
  const startDate = form.watch(`variants.${index}.startDate`);
  const grind = form.watch(`variants.${index}.grind`);
  const isSelected = form.watch(`variants.${index}.isProductSelectedForImport`);

  // Ship-to is entered on the whole-bean variant and inherited by other grinds of
  // the same bag size — mirroring price/term. Only the whole-bean row
  // renders the editable Ship-To selector; other grinds show it read-only.
  const isWholeBeanVariant = grind?.includes(REQUIRED_GRIND) ?? false;
  const hasShipToOptions = !!shipToOptions && shipToOptions.length > 0;

  // Reproduce the product-level home-country lock at variant level: the roaster's
  // own country is always a selected (and unremovable) ship-to destination on the
  // whole-bean variant, so a cross-border bag size never ends up with zero
  // destinations. Non-editable when the bag size is already imported.
  React.useEffect(() => {
    if (!isWholeBeanVariant || !hasShipToOptions || !homeRegion || variant?.isBagSizeVariantImported) {
      return;
    }
    const homeSelectable = shipToOptions?.some((option) => option.value === homeRegion) ?? false;
    if (!homeSelectable) {
      return;
    }
    const current = form.getValues(`variants.${index}.ship_to`) ?? "";
    const codes = current ? current.split(";").filter(Boolean) : [];
    if (!codes.includes(homeRegion)) {
      form.setValue(`variants.${index}.ship_to`, [homeRegion, ...codes].join(";"), { shouldDirty: false });
    }
  }, [isWholeBeanVariant, hasShipToOptions, homeRegion, shipToOptions, form, index, variant?.isBagSizeVariantImported]);

  // Watch override flags so setValue triggers re-render for grind/bagSize select toggling
  const hasDetectedGrindOverride = form.watch(`variants.${index}.hasDetectedGrindOverride`);
  const hasDetectedBagSizeOverride = form.watch(`variants.${index}.hasDetectedBagSizeOverride`);

  const [{ config }] = useConfigState();
  const marginFloorPercent = config.marginConfig?.marginPercentage;

  const watchedPrice = form.watch(`variants.${index}.price`);
  const watchedWholesalePrice = form.watch(`variants.${index}.wholesalePrice`);
  const msrp = typeof watchedPrice === "number" ? watchedPrice : Number.parseFloat(String(watchedPrice ?? ""));
  const wholesale =
    typeof watchedWholesalePrice === "number" ? watchedWholesalePrice : Number.parseFloat(String(watchedWholesalePrice ?? ""));

  // Live margin check — bypasses onBlur so the error appears on each keystroke.
  const marginFloorError = React.useMemo(() => {
    if (!marginFloorPercent || marginFloorPercent <= 0) return null;
    if (!Number.isFinite(msrp) || !Number.isFinite(wholesale) || msrp <= 0 || wholesale <= 0) return null;
    const margin = calculateMarginPercent(msrp, wholesale);
    return margin < marginFloorPercent
      ? t("views.shopifyImportModal.productForm.validation.marginTooLow", { percent: marginFloorPercent, current: margin.toFixed(1) })
      : null;
  }, [marginFloorPercent, msrp, wholesale, t]);

  // Clear stale Zod margin error when margin recovers; onBlur won't re-run Zod
  // mid-edit. Only clear the 'custom' type so required/positive errors are preserved.
  React.useEffect(() => {
    if (!marginFloorPercent || marginFloorError !== null) return;
    const error = form.getFieldState(`variants.${index}.wholesalePrice`).error;
    if (error?.type === "custom") form.clearErrors(`variants.${index}.wholesalePrice`);
  }, [marginFloorError, marginFloorPercent, form, index]);

  // Check if bag size group has duplicate grinds. Checks both the raw
  // Shopify-detected grind info (`detectedGrindInfo`) AND the variant's
  // current `grind` value — a variant can have a matching bag size but no
  // detected grind info at all (grind type not detected/mapped) while its
  // `grind` field already carries a value that duplicates one already
  // claimed elsewhere in the group; relying on `detectedGrindInfo` alone
  // missed that case.
  const hasDuplicateGrindInBagSizeGroup = React.useMemo(() => {
    if (!variant || !bagSizeGroup) return false;
    const detectedBagSize = variant.mapDetectedBagSizeTo ?? variant.bagSize;

    if (variant.isProductSelectedForImport || !variant.hasDetectedBagSize || detectedBagSize !== bagSizeGroup.bagSize) {
      return false;
    }

    const detectedGrindMatches = (variant.detectedGrindInfo ?? []).some((detectedGrind) =>
      bagSizeGroup.grindTypes.includes(detectedGrind.mapDetectedGrindTo ?? detectedGrind.grind)
    );
    const currentGrindMatches = (variant.grind ?? []).some((grind) => bagSizeGroup.grindTypes.includes(grind));

    return detectedGrindMatches || currentGrindMatches;
  }, [variant, bagSizeGroup]);

  // Check if term override required for large bags
  const hasTermOverrideForLargeBag = React.useMemo(() => {
    if (!regionLargeBagSize || !variant) return false;
    const bagSize = variant.mapDetectedBagSizeTo ?? variant.bagSize;
    return regionLargeBagSize.includes(bagSize);
  }, [regionLargeBagSize, variant]);

  // Determine if variant checkbox is disabled
  const isVariantDisabled = React.useMemo(() => {
    if (!variant) return false;

    // PIM/live variants are already created and remain locked even if
    // Salesforce still has stale rejected metadata for the same SKU.
    if (variant.isImportedLocalVariant) {
      return true;
    }

    // Allow override
    if (variant.hasDetectedBagSizeOverride || variant.hasDetectedGrindOverride) {
      return false;
    }

    // Allow rejected variants
    if (isRejectedVariant(variant)) {
      return false;
    }

    // Disable imported remote variants
    if (variant.isImportedRemote) {
      return true;
    }

    // Disable invalid detected bag size or grind
    if (
      (variant.hasDetectedBagSize && !variant.hasValidDetectedBagSize) ||
      (variant.hasDetectedGrindType && !variant.hasValidDetectedGrindType)
    ) {
      return true;
    }

    // Disable if duplicate grind in bag size group
    if (hasDuplicateGrindInBagSizeGroup) {
      return true;
    }

    // Single bag size mode logic
    if (!singleBagSize) {
      return false;
    }

    if (variant.hasDetectedBagSize && variant.bagSize !== singleBagSize) {
      return true;
    } else if (variant.hasDetectedBagSize && variant.bagSize === singleBagSize) {
      return false;
    }

    return false;
  }, [variant, hasDuplicateGrindInBagSizeGroup, singleBagSize]);

  // Show the mapped-grind hint only when the detected grind will actually be
  // remapped on import. Suppress it when the user has explicitly overridden the
  // grind (downstream import uses variant.grind directly) or when a different
  // duplicate-grind error already owns the label.
  const showMappedGrindsHint = React.useMemo(() => {
    if (!variant || hasDetectedGrindOverride || hasDuplicateGrindInBagSizeGroup) {
      return false;
    }
    return (variant.detectedGrindInfo ?? []).some((grind) => !!grind.mapDetectedGrindTo);
  }, [variant, hasDetectedGrindOverride, hasDuplicateGrindInBagSizeGroup]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const helperMessages = React.useMemo<readonly MappedGrindMessage[]>(() => {
    if (!showMappedGrindsHint) return EMPTY_MESSAGES;
    return (variant?.detectedGrindInfo ?? [])
      .filter((grind) => grind.mapDetectedGrindTo)
      .map((grind, i) => {
        const translationKey =
          i === 0 ? "components.SingleVariantForm.variantGrindMapped" : "components.SingleVariantForm.variantGrindMappedMultiple";
        return {
          key: `${grind.grind}→${grind.mapDetectedGrindTo}`,
          text: t(translationKey, {
            grindType: grind.grind,
            detectedGrind: grind.mapDetectedGrindTo,
          }),
        };
      });
  }, [showMappedGrindsHint, variant?.detectedGrindInfo, t]);

  // Determine checkbox label style
  const labelStyle = React.useMemo(() => {
    if (!variant) return "default";

    if (variant.isImportedLocalVariant) {
      return "success";
    }
    if (isRejectedVariant(variant)) {
      return "error";
    }
    if (variant.isImportedRemote) {
      return "default";
    }
    if (showMappedGrindsHint) {
      return "warning";
    }
    if (variant.hasDetectedBagSize && variant.bagSize !== singleBagSize && hasDuplicateGrindInBagSizeGroup) {
      return "error";
    }
    return "default";
  }, [variant, showMappedGrindsHint, hasDuplicateGrindInBagSizeGroup, singleBagSize]);

  // Determine checkbox label helper text (status badge). Ordering matters:
  // live / rejected / imported / invalid / duplicate states must win over the
  // mapped-grind hint, which is the lowest-priority helper.
  const labelHelper = React.useMemo<readonly MappedGrindMessage[] | undefined>(() => {
    if (!variant) return undefined;

    if (variant.isImportedLocalVariant) {
      return [
        {
          key: "variantIsImportedLocal",
          text: t("components.SingleVariantForm.variantIsImportedLocal"),
        },
      ];
    }
    if (isRejectedVariant(variant)) {
      return [
        {
          key: "variantIsRejected",
          text: t("components.SingleVariantForm.variantIsRejected"),
        },
      ];
    }
    if (variant.isImportedRemote) {
      return [
        {
          key: "variantIsImported",
          text: t("components.SingleVariantForm.variantIsImported"),
        },
      ];
    }
    if (variant.hasDetectedBagSize && !variant.hasValidDetectedBagSize) {
      return [
        {
          key: "bagSizeNotAllowed",
          text: t("components.SingleVariantForm.bagSizeNotAllowed"),
        },
      ];
    }
    if (hasDuplicateGrindInBagSizeGroup) {
      return [
        {
          key: "onlyOneGrindPerBag",
          text: t("components.SingleVariantForm.onlyOneGrindPerBag"),
        },
      ];
    }
    if (variant.hasDetectedGrindType && !variant.hasValidDetectedGrindType) {
      return [
        {
          key: "grindTypeNotAllowed",
          text: t("components.SingleVariantForm.grindTypeNotAllowed"),
        },
      ];
    }
    if (!variant.isImportedRemote && isVariantDisabled) {
      return [
        {
          key: "variantIsDisabledSingleBagSize",
          text: t("components.SingleVariantForm.variantIsDisabledSingleBagSize"),
        },
      ];
    }
    if (showMappedGrindsHint) {
      return helperMessages;
    }
    return undefined;
  }, [variant, hasDuplicateGrindInBagSizeGroup, isVariantDisabled, showMappedGrindsHint, helperMessages, t]);

  // Badge variant, className, and icon derived from labelStyle
  const labelBadgeProps = React.useMemo(() => {
    switch (labelStyle) {
      case "success":
        return {
          variant: "outline" as const,
          className: "border-green-600/30 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400",
          icon: <CheckCircle2 className="size-3" />,
        };
      case "error":
        return {
          variant: "destructive" as const,
          className: "",
          icon: <XCircle className="size-3" />,
        };
      case "warning":
        return {
          variant: "outline" as const,
          className: "border-orange-600/30 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400",
          icon: <AlertTriangle className="size-3" />,
        };
      default:
        return {
          variant: "secondary" as const,
          className: "",
          icon: <CheckCircle2 className="size-3" />,
        };
    }
  }, [labelStyle]);

  // Detected invalid grinds
  const detectedInvalidGrinds = React.useMemo(() => {
    if (!variant) return [];
    return (variant.detectedGrindInfo ?? [])
      .filter((detectedGrind) => !detectedGrind.isValidGrind)
      .map((detectedGrind) => detectedGrind.grind);
  }, [variant]);

  // Info block visibility
  const isInfoBlockVisible = React.useMemo(() => {
    if (!variant) return false;
    if (!isOverrideGrindEnabled && !isOverrideBagSizeEnabled) return false;
    if (variant.isImportedLocalVariant || isVariantDisabled) {
      return false;
    }
    if (variant.isImportedRemote) {
      return isRejectedVariant(variant) && isOverrideGrindEnabled;
    }
    return variant.hasDetectedBagSize || variant.hasDetectedGrindType;
  }, [variant, isOverrideGrindEnabled, isOverrideBagSizeEnabled, isVariantDisabled]);

  // Bag size select enabled — use watched hasDetectedBagSizeOverride for reactivity
  const isBagSizeSelectEnabled = React.useMemo(() => {
    if (!variant) return false;
    if (variant.hasDetectedBagSize && !hasDetectedBagSizeOverride) return false;
    if (hasDetectedBagSizeOverride) return true;
    return true;
  }, [variant, hasDetectedBagSizeOverride]);

  // Grind select enabled — use watched hasDetectedGrindOverride for reactivity
  const isGrindSelectEnabled = React.useMemo(() => {
    if (!variant) return false;
    if (variant.hasDetectedGrindType && !hasDetectedGrindOverride) return false;
    if (hasDetectedGrindOverride) return true;
    return true;
  }, [variant, hasDetectedGrindOverride]);

  // Bag size options — exclude sizes already imported on this product (e.g.
  // the SS "New Bag Size" flow passes the SKU's existing variants in
  // `excludedBagSizes`). Keep the current selection so a roaster switching
  // between values never sees their own pick disappear mid-edit.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const bagSizeOptions = React.useMemo(() => {
    const excluded = new Set(excludedBagSizes ?? []);
    return productConfig.bagSize
      .filter((bagSize) => !excluded.has(bagSize) || variant?.bagSize === bagSize)
      .map((bagSize) => {
        const isDisabled = selectedVariantCount > 1 && singleBagSize && singleBagSize !== bagSize && variant?.bagSize !== bagSize;
        return {
          label: bagSize,
          value: bagSize,
          isDisabled: isDisabled ?? false,
        };
      });
  }, [productConfig.bagSize, selectedVariantCount, singleBagSize, variant?.bagSize, excludedBagSizes]);

  // Weekly inventory min/max based on term
  const { minWeeklyInventory, maxWeeklyInventory } = React.useMemo(() => {
    if (term === "Short") {
      return {
        minWeeklyInventory: shortTermMinWeeklyInventory,
        maxWeeklyInventory: shortTermMaxWeeklyInventory,
      };
    } else if (term === "Long") {
      return {
        minWeeklyInventory: longTermMinWeeklyInventory,
        maxWeeklyInventory: longTermMaxWeeklyInventory,
      };
    }
    return { minWeeklyInventory: undefined, maxWeeklyInventory: undefined };
  }, [term, shortTermMinWeeklyInventory, shortTermMaxWeeklyInventory, longTermMinWeeklyInventory, longTermMaxWeeklyInventory]);

  // Date constraints for end date
  const minimumEndOfStockDate = React.useMemo(() => {
    if (!startDate) return undefined;
    const date = parse(startDate, "yyyy-MM-dd", new Date());
    return addDays(date, MINIMUM_END_OF_STOCK_DATE_DAYS);
  }, [startDate]);

  const maximumEndOfStockDate = React.useMemo(() => {
    if (!startDate) return undefined;
    const date = parse(startDate, "yyyy-MM-dd", new Date());
    return addDays(date, MAXIMUM_END_OF_STOCK_DATE_DAYS);
  }, [startDate]);

  // Override handlers
  const overriddenBagSize = React.useRef<string | undefined>(variant?.bagSize);
  const overriddenGrind = React.useRef<string[] | undefined>(variant?.grind);

  // Keep refs in sync with variant data
  React.useEffect(() => {
    if (variant?.bagSize && !variant?.hasDetectedBagSizeOverride) {
      overriddenBagSize.current = variant.bagSize;
    }
  }, [variant?.bagSize, variant?.hasDetectedBagSizeOverride]);

  React.useEffect(() => {
    if (variant?.grind && !variant?.hasDetectedGrindOverride) {
      overriddenGrind.current = variant.grind;
    }
  }, [variant?.grind, variant?.hasDetectedGrindOverride]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const handleOverrideBagSize = React.useCallback(() => {
    if (!variant?.hasDetectedBagSizeOverride) {
      form.setValue(`variants.${index}.bagSize`, null as unknown as string, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue(`variants.${index}.bagSize`, overriddenBagSize.current ?? "", { shouldDirty: true, shouldValidate: true });
    }
    form.setValue(`variants.${index}.hasDetectedBagSizeOverride`, !variant?.hasDetectedBagSizeOverride, { shouldDirty: true });
  }, [form, index, variant?.hasDetectedBagSizeOverride]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const handleOverrideGrind = React.useCallback(() => {
    if (!variant?.hasDetectedGrindOverride) {
      form.setValue(`variants.${index}.grind`, [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue(`variants.${index}.grind`, overriddenGrind.current ?? [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    form.setValue(`variants.${index}.hasDetectedGrindOverride`, !variant?.hasDetectedGrindOverride, { shouldDirty: true });
  }, [form, index, variant?.hasDetectedGrindOverride]);

  const handleGrindChange = React.useCallback(
    (newValue: string[]) => {
      form.setValue(`variants.${index}.grind`, newValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [form, index]
  );

  const handleBagSizeChange = React.useCallback(
    (value: string) => {
      if (regionLargeBagSize?.includes(value) && isProductImportedLocal && term === "Long" && hasTermOverrideForLargeBag) {
        form.setValue(`variants.${index}.term`, "Short");
      }

      if (regionLargeBagSize?.includes(value) && !term && !variant.hasDetectedBagSize) {
        form.setValue(`variants.${index}.term`, "Short");
      }

      form.setValue(`variants.${index}.bagSize`, value);

      // Auto-add Wholebean to grind when a bag size is selected
      const currentGrind = form.getValues(`variants.${index}.grind`) ?? [];
      if (!currentGrind.includes(REQUIRED_GRIND)) {
        form.setValue(`variants.${index}.grind`, [REQUIRED_GRIND, ...currentGrind]);
      }
    },
    [form, hasTermOverrideForLargeBag, index, isProductImportedLocal, regionLargeBagSize, term, variant.hasDetectedBagSize]
  );

  // Show alert for auto-mapped whole bean variants
  const showAlertMessage = React.useMemo(() => {
    if (!variant) return false;
    const grindIsWholeBean = grind?.includes(REQUIRED_GRIND) ?? false;
    if (hasDetectedGrindOverride) {
      // After override, only suppress the alert when the user has selected Whole Bean.
      return !grindIsWholeBean;
    }
    const isBagSizeNotDefaultGrindType = variant.hasValidDetectedGrindType && !grindIsWholeBean;
    return variant.isVariantHasSameBagSize && !variant.isBagSizeVariantImported && isBagSizeNotDefaultGrindType;
  }, [variant, grind, hasDetectedGrindOverride]);

  React.useEffect(() => {
    if (!variant) {
      return;
    }

    const shouldSyncSharedValues = !!bagSizeGroup && (variant.isBagSizeVariantImported || showAlertMessage);

    if (!shouldSyncSharedValues) {
      return;
    }
    applySharedBagSizeValues(form, index, variant, bagSizeGroup, !!showAlertMessage);
  }, [bagSizeGroup, form, index, showAlertMessage, variant]);

  if (!variant) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-lg border p-4" data-selected={isSelected}>
      {/* 1. Selection Checkbox */}
      <FormField
        control={form.control}
        name={`variants.${index}.isProductSelectedForImport`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={!!field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                disabled={isVariantDisabled}
                testId={setSingleVariantFormTestId("checkbox", index)}
              />
            </FormControl>
            <div className={cn("flex-1 flex flex-wrap items-center gap-2", isVariantDisabled && "opacity-60")}>
              <FormLabel
                className={cn(
                  "text-base leading-none",
                  isVariantDisabled && "text-muted-foreground",
                  labelStyle === "error" && "text-destructive",
                  labelStyle === "success" && "text-green-600",
                  labelStyle === "warning" && "text-orange-600"
                )}
              >
                {variant.title}
              </FormLabel>
              {labelHelper?.map(({ key, text }) => (
                <Badge key={key} variant={labelBadgeProps.variant} className={labelBadgeProps.className}>
                  {labelBadgeProps.icon}
                  {text}
                </Badge>
              ))}
            </div>
          </FormItem>
        )}
      />

      {/* 2. VariantInfoBlock */}
      {isInfoBlockVisible && (
        <VariantInfoBlock
          variant={variant}
          onOverrideBagSize={handleOverrideBagSize}
          onOverrideGrind={handleOverrideGrind}
          // eslint-disable-next-line react-hooks/refs -- read-only ref access; current value flows to child for same-render display, write happens in earlier effect
          overriddenGrinds={overriddenGrind.current}
          // eslint-disable-next-line react-hooks/refs -- read-only ref access; current value flows to child for same-render display, write happens in earlier effect
          overriddenBagSize={overriddenBagSize.current}
          isOverrideGrindEnabled={isOverrideGrindEnabled}
          isOverrideBagSizeEnabled={isOverrideBagSizeEnabled}
          detectedInvalidGrinds={detectedInvalidGrinds}
        />
      )}

      {/* Alert for whole bean auto-mapping */}
      {isSelected && showAlertMessage && (
        <Alert>
          <AlertDescription>
            {t("components.SingleVariantForm.variantAutoMappingNotice", {
              bagSize: variant.bagSize,
              grindType: REQUIRED_GRIND,
            })}
          </AlertDescription>
        </Alert>
      )}

      {/* Fields only visible when variant is selected */}
      {isSelected && (
        <div className="space-y-4">
          {/* 3. Bag Size Select */}
          {isBagSizeSelectEnabled && (
            <FormField
              control={form.control}
              name={`variants.${index}.bagSize`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("components.ProductForm.bagSize")}{" "}
                    <span className="text-destructive" aria-hidden="true">
                      *
                    </span>
                  </FormLabel>
                  <Select onValueChange={handleBagSizeChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("components.ProductForm.selectBagSize")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {bagSizeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} disabled={option.isDisabled ? true : undefined}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* 3a. Read-only detected bag size display */}
          {!isBagSizeSelectEnabled && variant.hasDetectedBagSize && (
            <div>
              <FormLabel>{t("components.ProductForm.bagSize")}</FormLabel>
              <div className="text-sm text-muted-foreground mt-1">{variant.mapDetectedBagSizeTo || variant.bagSize}</div>
            </div>
          )}

          {/* 4. Grind Checkboxes */}
          {isGrindSelectEnabled && (
            <FormField
              control={form.control}
              name={`variants.${index}.grind`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("components.ProductForm.grind")}{" "}
                    <span className="text-destructive" aria-hidden="true">
                      *
                    </span>
                  </FormLabel>
                  <div className="flex flex-wrap gap-2" data-testid={setSingleVariantFormTestId("grind-options", index)}>
                    {grindConfig.map((grind) => {
                      const isChecked = field.value?.includes(grind.value) ?? false;
                      const isDisabled = !isChecked && (bagSizeGroup?.grindTypes.includes(grind.value) ?? false);

                      return (
                        <button
                          key={grind.value}
                          type="button"
                          aria-pressed={isChecked}
                          tabIndex={isDisabled ? -1 : 0}
                          disabled={isDisabled}
                          data-testid={setSingleVariantFormTestId(`grind-option-${grind.value.replace(/\s+/g, "")}`, index)}
                          className={cn(
                            "cursor-pointer select-none bg-transparent border-0 p-0",
                            isDisabled && "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => {
                            if (isDisabled) return;
                            const currentValue = field.value ?? [];
                            handleGrindChange(toggleGrindSelection(currentValue, isChecked, grind.value));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (isDisabled) return;
                              const currentValue = field.value ?? [];
                              handleGrindChange(toggleGrindSelection(currentValue, isChecked, grind.value));
                            }
                          }}
                        >
                          <Badge variant={isChecked ? "default" : "outline"}>{grind.label}</Badge>
                        </button>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* 4a. Read-only detected grind display */}
          {!isGrindSelectEnabled && variant.hasDetectedGrindType && (
            <div>
              <FormLabel>{t("components.ProductForm.grind")}</FormLabel>
              <div className="flex flex-wrap gap-2 mt-1">
                {variant.grind?.map((grindValue) => {
                  const grindLabel = grindConfig.find((g) => g.value === grindValue)?.label ?? grindValue;
                  return (
                    <Badge key={grindValue} variant="secondary">
                      {grindLabel}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4b. Ship to (cross-border) — bag-size based. Editable on
              the whole-bean variant; other grinds of the same bag size inherit it
              and show it read-only. Only for cross-border vendors (options set).
              The home country is always selected and locked (`isDisabled`). */}
          {hasShipToOptions && isWholeBeanVariant && (
            <FormField
              control={form.control}
              name={`variants.${index}.ship_to`}
              render={({ field }) => {
                const selected = field.value ? field.value.split(";").filter(Boolean) : [];
                const selectedWithHome = homeRegion && !selected.includes(homeRegion) ? [homeRegion, ...selected] : selected;

                return (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.shipTo")}</FormLabel>
                    <div className="space-y-2">
                      <MultiSelect
                        options={shipToOptions ?? []}
                        value={selectedWithHome}
                        onChange={(values) => field.onChange(values.join(";"))}
                        disabled={variant.isBagSizeVariantImported}
                        placeholder={t("views.shopifyImportModal.productForm.placeholders.selectMultiple")}
                        searchPlaceholder={t("components.multiSelect.searchPlaceholder")}
                        emptyMessage={t("components.multiSelect.emptyMessage")}
                        clearAllText={t("components.multiSelect.clearAll")}
                        identifier={setSingleVariantFormTestId("ship-to", index)}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          {/* 4c. Read-only inherited ship-to for non-whole-bean grinds of the
              same bag size — mirrors how price/term display as inherited. */}
          {hasShipToOptions && !isWholeBeanVariant && !!bagSizeGroup?.ship_to && (
            <div>
              <FormLabel>{t("views.shopifyImportModal.productForm.labels.shipTo")}</FormLabel>
              <div className="flex flex-wrap gap-2 mt-1" data-testid={setSingleVariantFormTestId("ship-to-inherited", index)}>
                {parseShipToCountries(bagSizeGroup.ship_to).map((country) => (
                  <Badge key={country} variant="secondary">
                    {country}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {!showAlertMessage && (
            <>
              {/* 5. Price Fields — cross-border (home editable + derived) or the
                  single-region 2-column MSRP/Wholesale grid. */}
              {isCrossBorderPricing && crossBorderHomeRegion ? (
                <CrossBorderVariantPricing
                  index={index}
                  shipTo={effectiveShipTo}
                  homeRegion={crossBorderHomeRegion}
                  ratesByRegion={taxRatesByRegion}
                  isBagSizeVariantImported={variant.isBagSizeVariantImported}
                  marginFloorError={marginFloorError}
                />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`variants.${index}.price`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("components.ProductForm.msrp")}{" "}
                          <span className="text-destructive" aria-hidden="true">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            value={field.value ?? ""}
                            onChange={handleNumericFieldChange(field.onChange)}
                            disabled={variant.isBagSizeVariantImported}
                            testId={setSingleVariantFormTestId("msrp", index)}
                            aria-invalid={!!marginFloorError}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`variants.${index}.wholesalePrice`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("components.ProductForm.wholesalePrice")}{" "}
                          <span className="text-destructive" aria-hidden="true">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            value={field.value ?? ""}
                            onChange={handleNumericFieldChange(field.onChange)}
                            disabled={variant.isBagSizeVariantImported}
                            testId={setSingleVariantFormTestId("wholesale-price", index)}
                            aria-invalid={!!marginFloorError}
                          />
                        </FormControl>
                        {marginFloorError ? (
                          <div className="flex items-center gap-1.5 w-full">
                            <span className="flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-white text-[10px] font-bold shrink-0">
                              !
                            </span>
                            <span className="text-xs font-semibold text-destructive">{marginFloorError}</span>
                          </div>
                        ) : (
                          <FormMessage />
                        )}
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* 6. Term, 7. Start Date, 8. End Date (conditional), 9. Weekly Inventory */}
              <div className="grid grid-cols-3 gap-4">
                {/* 6. Term Select */}
                <FormField
                  control={form.control}
                  name={`variants.${index}.term`}
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>
                        {t("components.ProductForm.term")}{" "}
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={hasTermOverrideForLargeBag || variant.isBagSizeVariantImported}
                      >
                        <FormControl>
                          <SelectTrigger testId={setSingleVariantFormTestId("term", index)}>
                            <SelectValue placeholder={t("components.ProductForm.selectTerm")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {termConfig.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 7. Start Date */}
                <FormField
                  control={form.control}
                  name={`variants.${index}.startDate`}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        {t("components.ProductForm.startDate")}{" "}
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      </FormLabel>
                      <FormControl>
                        <DateSelect
                          name={`start-date-${index}`}
                          testId={setSingleVariantFormTestId("start-date", index)}
                          selectedDate={field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : undefined}
                          onChange={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                          exclude={["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]}
                          minDate={nextAvailableStartDay}
                          placeholder={t("components.ProductForm.pickDate")}
                          format="PPP"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 8. End Date (only for Short term) */}
                {term === "Short" && (
                  <FormField
                    control={form.control}
                    name={`variants.${index}.endDate`}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {t("components.ProductForm.endDate")}{" "}
                          <span className="text-destructive" aria-hidden="true">
                            *
                          </span>
                        </FormLabel>
                        <FormControl>
                          <DateSelect
                            name={`end-date-${index}`}
                            testId={setSingleVariantFormTestId("end-date", index)}
                            selectedDate={field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : undefined}
                            onChange={(date) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                            minDate={minimumEndOfStockDate}
                            maxDate={maximumEndOfStockDate}
                            placeholder={t("components.ProductForm.pickDate")}
                            format="PPP"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* 9. Weekly Inventory (spans appropriate columns) */}
                <FormField
                  control={form.control}
                  name={`variants.${index}.weeklyInventory`}
                  render={({ field }) => (
                    <FormItem className={term === "Short" ? "col-span-3" : ""}>
                      <FormLabel>
                        {t("components.ProductForm.weeklyInventory")}{" "}
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="50"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value ? Number.parseInt(e.target.value, 10) : null)}
                          disabled={!term || variant.isBagSizeVariantImported}
                          testId={setSingleVariantFormTestId("weekly-inventory", index)}
                        />
                      </FormControl>
                      {minWeeklyInventory !== undefined && maxWeeklyInventory !== undefined && (
                        <FormDescription>
                          {t("components.ProductForm.inventoryRange", {
                            min: minWeeklyInventory,
                            max: maxWeeklyInventory,
                          })}
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 10. Total Max Short Term Inventory (only for Short term) */}
              {term === "Short" && (
                <FormField
                  control={form.control}
                  name={`variants.${index}.totalMaxShortTermInventory`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("components.ProductForm.shortTermTotalInventory")}{" "}
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="50"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value ? Number.parseInt(e.target.value, 10) : null)}
                          disabled={!term || variant.isBagSizeVariantImported}
                          testId={setSingleVariantFormTestId("total-max-inventory", index)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
